import React, { memo, useState } from 'react';

import {
  AutoWizard,
  BtnToolbar,
  Button,
  ButtonAction,
  Combobox,
  HookForm,
  PageTitle
} from '@dec/shared';
import { ButtonGroup, Card, Col, ListGroup, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useGetEnabledRegistrationFields } from '@/app/modules/Parameters/TaxpayerFieldRegistration/services/hooks';

import { useTranslateListAll } from '../../../../../components/translate/useTranslateList';
import { ObjectRequired } from '../../../../../core/validations/common';
import { useTaxpayerRequestStore } from '../../../stores/useTaxpayerRequestStore';
import { type TaxpayerRegistrationFieldConfiguration } from '../../../TaxpayerRequestEvaluation/types';
import { useTaxpayerRequestCapabilities } from '../../hooks';
import { label } from '../../translation';

const StepWizard = AutoWizard;
export const TaxpayerRegistrationContext =
  React.createContext<TaxpayerRegistrationFieldConfiguration>({ fields: [] });

const Step = (props: any) => {
  return (
    <li className={`list-group-item ${props.currentStep === props.stepKey ? 'active' : ''}`}>
      {props.label}
    </li>
  );
};

export default Step;

const Nav = (props: any) => {
  const { t } = useTranslation();
  const documentsStep = props.reportsEnabled ? 7 : 6;
  const acceptanceStep = documentsStep + (props.attachmentsEnabled ? 1 : 0);

  return (
    <>
      <div className="my-4">
        <ListGroup horizontal className="d-none d-lg-flex">
          <Step {...props} stepKey={1} label={label(t, 'generalData')} />
          <Step {...props} stepKey={2} label={label(t, 'tax')} />
          <Step {...props} stepKey={3} label={label(t, 'contacts')} />
          <Step {...props} stepKey={4} label={label(t, 'operations')} />
          <Step {...props} stepKey={5} label={label(t, 'taxVector')} />
          {props.reportsEnabled && <Step {...props} stepKey={6} label={label(t, 'reportVector')} />}
          {props.attachmentsEnabled && (
            <Step {...props} stepKey={documentsStep} label={label(t, 'documents')} />
          )}
          <Step {...props} stepKey={acceptanceStep} label={label(t, 'acceptance')} />
        </ListGroup>
        <ListGroup className="my-4 d-inline d-lg-none">
          <Step {...props} stepKey={1} label={label(t, 'generalData')} />
          <Step {...props} stepKey={2} label={label(t, 'tax')} />
          <Step {...props} stepKey={3} label={label(t, 'contacts')} />
          <Step {...props} stepKey={4} label={label(t, 'operations')} />
          <Step {...props} stepKey={5} label={label(t, 'taxVector')} />
          {props.reportsEnabled && <Step {...props} stepKey={6} label={label(t, 'reportVector')} />}
          {props.attachmentsEnabled && (
            <Step {...props} stepKey={documentsStep} label={label(t, 'documents')} />
          )}
          <Step {...props} stepKey={acceptanceStep} label={label(t, 'acceptance')} />
        </ListGroup>
      </div>
    </>
  );
};

interface WizardProps {
  conf: TaxpayerRegistrationFieldConfiguration;
}

const Wizard = ({ conf }: WizardProps) => {
  const { loading, error, reportsEnabled, attachmentsEnabled } = useTaxpayerRequestCapabilities();

  if (loading || error) {
    return <></>;
  }

  return <TaxpayerRegistrationContext.Provider value={conf}></TaxpayerRegistrationContext.Provider>;
};

export const TaxpayerRequest = memo(() => {
  const [{ data, loading, error }] = useGetEnabledRegistrationFields();
  const preferredLanguage = useTaxpayerRequestStore((s) => s.form.preferredLanguage);
  const langList = useTranslateListAll();
  const { t, i18n } = useTranslation();
  const [languageSelected, setLanguageSelected] = useState(false);
  const rules = yup.object().shape({
    locale: ObjectRequired
  });
  const navigate = useNavigate();

  const cancel = () => {
    navigate('/');
  };

  if (!languageSelected) {
    return (
      <div className="col-12 col-md-6 col-lg-5 mx-auto">
        <Card className="p-2">
          <Card.Body>
            <HookForm
              nameForm={'formSelectLang'}
              validations={rules}
              onSubmit={() => {
                setLanguageSelected(true);
              }}
            >
              <Row>
                <Col>
                  <Combobox
                    code="id"
                    display="name"
                    data={langList}
                    label={label(t, 'locale')}
                    name="locale"
                    defaultValue={
                      preferredLanguage
                        ? langList.find((x) => x.id == preferredLanguage)
                        : undefined
                    }
                    whenChange={(e: any) => {
                      if (e?.id && i18n.language !== e.id) {
                        i18n.changeLanguage(e.id);
                      }
                    }}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <BtnToolbar>
                    <ButtonGroup>
                      <Button action={ButtonAction.cancel} onClick={() => cancel()} />
                    </ButtonGroup>
                    <ButtonGroup>
                      <Button
                        action={ButtonAction.next}
                        variant="primary"
                        form="formSelectLang"
                        type={'submit'}
                        text={t('common.button.continue')}
                      />
                    </ButtonGroup>
                  </BtnToolbar>
                </Col>
              </Row>
            </HookForm>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (loading || error) {
    return (
      <div className="container content-body">
        <Wizard conf={{ fields: [] }} />
      </div>
    );
  }

  return (
    <>
      <div className="container content-body">
        <PageTitle title={t('taxpayer-request.label.taxpayerRequest')} />

        <Wizard conf={{ fields: data || [] }} />
      </div>
    </>
  );
});

TaxpayerRequest.displayName = 'TaxpayerRequest';
